import React from 'react'
import type { DashboardMetrics } from '../../hooks/useDashboard'
import { Card } from '../ui/Card'

interface KPITilesProps {
  kpis: DashboardMetrics['kpis']
  loading?: boolean
}

export const KPITiles: React.FC<KPITilesProps> = ({ kpis, loading }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`
  }

  const formatDelta = (value: number) => {
    const sign = value >= 0 ? '+' : ''
    return `${sign}${value.toFixed(1)}%`
  }

  const getDeltaColor = (value: number) => {
    if (value > 0) return 'text-success-600'
    if (value < 0) return 'text-danger-600'
    return 'text-muted'
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => (
          <Card key={i} className="p-4">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-2 w-24"></div>
              <div className="h-8 bg-gray-200 rounded mb-2 w-32"></div>
              <div className="h-3 bg-gray-200 rounded w-20"></div>
            </div>
          </Card>
        ))}
      </div>
    )
  }

  const tiles = [
    {
      label: 'Total Orders',
      value: kpis.total_orders_today.toLocaleString(),
      delta: kpis.delta_orders,
      icon: '📦',
      iconBg: 'bg-blue-50',
      iconColor: 'text-blue-600'
    },
    {
      label: 'Delivered Orders',
      value: kpis.delivered_orders_today.toLocaleString(),
      subtitle: `${formatPercent(kpis.completion_rate)} completion`,
      icon: '✅',
      iconBg: 'bg-green-50',
      iconColor: 'text-green-600'
    },
    {
      label: 'Cancelled Orders',
      value: kpis.cancelled_orders_today.toLocaleString(),
      subtitle: `${formatPercent(kpis.cancellation_rate)} cancel rate`,
      icon: '❌',
      iconBg: 'bg-red-50',
      iconColor: 'text-red-600'
    },
    {
      label: 'Gross Revenue',
      value: formatCurrency(kpis.gross_revenue_today),
      delta: kpis.delta_revenue,
      subtitle: `Net: ${formatCurrency(kpis.net_revenue_today)} (delivered only)`,
      icon: '💰',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600'
    },
    {
      label: 'Delivery Fee Revenue',
      value: formatCurrency(kpis.delivery_fee_revenue_today),
      subtitle: 'Total delivery fees today',
      icon: '🛵',
      iconBg: 'bg-cyan-50',
      iconColor: 'text-cyan-600'
    },
    {
      label: 'Avg Order Value',
      value: formatCurrency(kpis.avg_order_value),
      icon: '📊',
      iconBg: 'bg-purple-50',
      iconColor: 'text-purple-600'
    },
    {
      label: 'Active Restaurants',
      value: kpis.active_restaurants_today.toLocaleString(),
      subtitle: 'Received orders today',
      icon: '🏪',
      iconBg: 'bg-orange-50',
      iconColor: 'text-orange-600'
    },
    {
      label: 'Active Users',
      value: kpis.active_users_today.toLocaleString(),
      subtitle: 'Placed orders today',
      icon: '👥',
      iconBg: 'bg-indigo-50',
      iconColor: 'text-indigo-600'
    }
  ]

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {tiles.map((tile, index) => (
        <Card key={index} className="p-5 hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between mb-3">
            <div className={`w-10 h-10 rounded-lg ${tile.iconBg} flex items-center justify-center text-xl`}>
              <span className={tile.iconColor}>{tile.icon}</span>
            </div>
            {tile.delta !== undefined && (
              <span className={`text-xs font-medium ${getDeltaColor(tile.delta)}`}>
                {formatDelta(tile.delta)}
              </span>
            )}
          </div>
          <p className="text-2xl font-semibold text-gray-900 mb-1">{tile.value}</p>
          <p className="text-sm text-gray-600">{tile.label}</p>
          {tile.subtitle && (
            <p className="text-xs text-gray-500 mt-1">{tile.subtitle}</p>
          )}
        </Card>
      ))}
    </div>
  )
}
